import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ColorPicker } from 'antd';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { usePanels } from '../../shared/hooks/use-panels';
import { useBackground } from '../hooks/use-background';
import { BTN, C, FONT_UI } from '../../constants/design-tokens';

export const BackgroundPanel: React.FC = () => {
  const { isOpen, close } = usePanels();
  const { activeResolution } = useResolutions();
  const { applyColor } = useBackground({
    designWidth: activeResolution.canvasWidth,
    designHeight: activeResolution.canvasHeight
  });
  const [solidColor, setSolidColor] = useState('#ffffff');
  const [applying, setApplying] = useState(false);

  if (!isOpen('background')) return null;

  const handleApply = async () => {
    setApplying(true);
    try {
      await applyColor(solidColor);
      close();
    } finally {
      setApplying(false);
    }
  };

  // Portal to <body> so the panel escapes ancestor transforms (e.g. the
  // right sidebar uses `transform: translateY(-50%)`, which would otherwise
  // scope `position: fixed` to the sidebar and push the panel off-screen).
  return createPortal(
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.35)',
          backdropFilter: 'blur(2px)',
          zIndex: 1000
        }}
      />

      <div
        data-panel-id='background'
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 280,
          background: C.panelBg,
          border: `1px solid ${C.panelBorder}`,
          borderRadius: 14,
          padding: 20,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
          fontFamily: FONT_UI,
          zIndex: 1001
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18
          }}
        >
          <h3 style={{ color: C.textPrimary, fontSize: 15, fontWeight: 600, margin: 0 }}>Color de fondo</h3>
          <button
            onClick={close}
            style={{
              width: 28,
              height: 28,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>

        <label
          style={{
            display: 'block',
            color: C.textSecondary,
            fontSize: 12,
            marginBottom: 8
          }}
        >
          Selecciona un color
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 8,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            marginBottom: 18
          }}
        >
          <ColorPicker
            value={solidColor}
            onChange={color => setSolidColor(color.toHexString())}
            showText={false}
            size='large'
            getPopupContainer={trigger => trigger.parentElement as HTMLElement}
          />
          <span
            style={{
              color: C.textPrimary,
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
              textTransform: 'uppercase'
            }}
          >
            {solidColor}
          </span>
        </div>

        <button
          onClick={handleApply}
          disabled={applying}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: BTN.primary.bg,
            border: 'none',
            borderRadius: 8,
            color: BTN.primary.text,
            fontSize: 13,
            fontWeight: 600,
            cursor: applying ? 'wait' : 'pointer',
            opacity: applying ? 0.6 : 1,
            transition: 'opacity 0.15s'
          }}
        >
          {applying ? 'Aplicando…' : 'Aplicar fondo'}
        </button>
      </div>
    </>,
    document.body
  );
};
